import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, unref as _unref, createVNode as _createVNode, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "relative" }
const _hoisted_2 = { class: "md:hidden" }
const _hoisted_3 = { class: "grid grid-cols-1 gap-6 items-start" }
const _hoisted_4 = { class: "hidden md:block" }
const _hoisted_5 = { class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start px-2" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = {
  key: 1,
  class: "flex justify-center items-center mt-6 space-x-2"
}
const _hoisted_9 = ["onClick", "aria-label"]
const _hoisted_10 = {
  key: 2,
  class: "text-center mt-3"
}
const _hoisted_11 = { class: "text-sm text-secondary-text dark:text-secondary-dark-text" }

import { ref, computed, watch } from 'vue'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'

import { onMounted, onUnmounted } from 'vue'

interface Props {
  items: any[]
  itemsPerSlide?: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ModuleCarousel',
  props: {
    items: {},
    itemsPerSlide: { default: 3 }
  },
  setup(__props: any) {

const props = __props

// State
const currentIndex = ref(0)
const carouselContainer = ref<HTMLElement | null>(null)
const isDragging = ref(false)
const dragOffset = ref(0)
const startX = ref(0)
const currentX = ref(0)

// Computed
const slides = computed(() => {
  const slideGroups: any[][] = []
  for (let i = 0; i < props.items.length; i += props.itemsPerSlide) {
    slideGroups.push(props.items.slice(i, i + props.itemsPerSlide))
  }
  return slideGroups
})

// Methods
const nextSlide = () => {
  if (currentIndex.value < slides.value.length - 1) {
    currentIndex.value++
  }
}

const previousSlide = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  }
}

const goToSlide = (index: number) => {
  currentIndex.value = index
}

// Mouse drag handlers
const handleDragStart = (event: MouseEvent) => {
  isDragging.value = true
  startX.value = event.pageX
  currentX.value = event.pageX
  
  // Prevent default drag behavior on images and links
  event.preventDefault()
}

const handleDragMove = (event: MouseEvent) => {
  if (!isDragging.value) return
  
  event.preventDefault()
  currentX.value = event.pageX
  dragOffset.value = currentX.value - startX.value
}

const handleDragEnd = () => {
  if (!isDragging.value) return
  
  const threshold = 50 // Minimum drag distance to trigger slide change
  
  if (dragOffset.value > threshold) {
    // Dragged right - go to previous slide
    previousSlide()
  } else if (dragOffset.value < -threshold) {
    // Dragged left - go to next slide
    nextSlide()
  }
  
  isDragging.value = false
  dragOffset.value = 0
}

// Touch handlers
const handleTouchStart = (event: TouchEvent) => {
  startX.value = event.touches[0].pageX
  currentX.value = event.touches[0].pageX
  isDragging.value = true
}

const handleTouchMove = (event: TouchEvent) => {
  if (!isDragging.value) return
  
  currentX.value = event.touches[0].pageX
  dragOffset.value = currentX.value - startX.value
}

const handleTouchEnd = () => {
  if (!isDragging.value) return
  
  const threshold = 50 // Minimum swipe distance to trigger slide change
  
  if (dragOffset.value > threshold) {
    // Swiped right - go to previous slide
    previousSlide()
  } else if (dragOffset.value < -threshold) {
    // Swiped left - go to next slide
    nextSlide()
  }
  
  isDragging.value = false
  dragOffset.value = 0
}

// Watch for items changes and reset to first slide
watch(() => props.items, () => {
  currentIndex.value = 0
})

// Optional: Add keyboard navigation
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowLeft') {
    previousSlide()
  } else if (event.key === 'ArrowRight') {
    nextSlide()
  }
}

// Add keyboard listeners when component is mounted
onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _renderSlot(_ctx.$slots, "mobile-view", { items: _ctx.items })
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", {
        class: "overflow-hidden cursor-grab active:cursor-grabbing",
        ref_key: "carouselContainer",
        ref: carouselContainer,
        onMousedown: handleDragStart,
        onMousemove: handleDragMove,
        onMouseup: handleDragEnd,
        onMouseleave: handleDragEnd,
        onTouchstart: handleTouchStart,
        onTouchmove: handleTouchMove,
        onTouchend: handleTouchEnd
      }, [
        _createElementVNode("div", {
          class: _normalizeClass(["flex transition-transform duration-500 ease-in-out", { 'transition-none': isDragging.value }]),
          style: _normalizeStyle({ transform: `translateX(calc(-${currentIndex.value * 100}% + ${dragOffset.value}px))` })
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slides.value, (slide, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: index,
              class: "w-full flex-shrink-0"
            }, [
              _createElementVNode("div", _hoisted_5, [
                _renderSlot(_ctx.$slots, `slide-${index}`, { items: slide })
              ])
            ]))
          }), 128))
        ], 6)
      ], 544),
      (slides.value.length > 1)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("button", {
              onClick: previousSlide,
              disabled: currentIndex.value === 0,
              class: _normalizeClass([
          'absolute left-0 top-[45%] -translate-y-1/2 -translate-x-4 z-10',
          'bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg',
          'border-2 border-gray-200 dark:border-gray-600',
          'transition-all duration-200',
          currentIndex.value === 0
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-xl hover:scale-110 cursor-pointer'
        ]),
              "aria-label": "Previous slide"
            }, [
              _createVNode(_unref(ChevronLeft), { class: "h-6 w-6 text-gray-700 dark:text-gray-300" })
            ], 10, _hoisted_6),
            _createElementVNode("button", {
              onClick: nextSlide,
              disabled: currentIndex.value === slides.value.length - 1,
              class: _normalizeClass([
          'absolute right-0 top-[45%] -translate-y-1/2 translate-x-4 z-10',
          'bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg',
          'border-2 border-gray-200 dark:border-gray-600',
          'transition-all duration-200',
          currentIndex.value === slides.value.length - 1
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-xl hover:scale-110 cursor-pointer'
        ]),
              "aria-label": "Next slide"
            }, [
              _createVNode(_unref(ChevronRight), { class: "h-6 w-6 text-gray-700 dark:text-gray-300" })
            ], 10, _hoisted_7)
          ], 64))
        : _createCommentVNode("", true),
      (slides.value.length > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slides.value, (slide, index) => {
              return (_openBlock(), _createElementBlock("button", {
                key: `dot-${index}`,
                onClick: ($event: any) => (goToSlide(index)),
                class: _normalizeClass([
          'transition-all duration-200',
          currentIndex.value === index
            ? 'bg-blue-600 dark:bg-blue-500 w-8 h-2 rounded-full'
            : 'bg-gray-300 dark:bg-gray-600 w-2 h-2 rounded-full hover:bg-gray-400 dark:hover:bg-gray-500'
        ]),
                "aria-label": `Go to slide ${index + 1}`
              }, null, 10, _hoisted_9))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (slides.value.length > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createElementVNode("span", _hoisted_11, _toDisplayString(currentIndex.value + 1) + " / " + _toDisplayString(slides.value.length), 1)
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})